// §2 — Contract Issues: the itemized asks from contract review, their
// resolution with the issuer (Sales) and internal acceptance (the raising
// department). Rules + payloads live in contractIssues.js; the parent owns the
// writes (sp_SaveContractIssue) and the notification emails.
import { useState } from "react";
import {
  ISSUE_OUTCOMES, ISSUE_STATUS, outcomeLabel, isMustResolve, issuesByReview,
  canActForReview, canResolveIssues, contractGate,
} from "./contractIssues";

// minHeight: 0 on the textareas overrides index.css's global 140px minimum,
// so their rows={} sizing applies in these compact forms.
const input = {
  padding: "6px 10px", fontSize: 12, border: "1px solid var(--cs-border)", borderRadius: 6,
  background: "var(--surface)", color: "var(--text)", boxSizing: "border-box", fontFamily: "inherit", width: "100%",
};
const btn = (primary) => ({
  padding: "5px 12px", fontSize: 12, fontWeight: 700, borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap",
  background: primary ? "var(--cs-blue)" : "none", color: primary ? "#fff" : "var(--text2)",
  border: primary ? "none" : "1px solid var(--cs-border)",
});
const when = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");

function StatusChip({ status }) {
  const s = ISSUE_STATUS[status] || ISSUE_STATUS.open;
  return <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 100, background: s.bg, color: s.color, whiteSpace: "nowrap" }}>{s.label}</span>;
}

function AddIssue({ review, onCreate, busy }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [must, setMust] = useState(true);
  const forced = review.Status === "Rejected";
  if (!open) return <button style={btn(false)} onClick={() => setOpen(true)}>+ Add issue</button>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: 10, background: "#f8f9fb", borderRadius: 6, marginTop: 6 }}>
      <textarea rows={2} value={text} onChange={e => setText(e.target.value)} placeholder="What must change, or what the project team must watch (one issue per entry)" style={{ ...input, resize: "vertical", minHeight: 0 }} />
      <label style={{ fontSize: 12, color: "var(--text2)", display: "flex", gap: 6, alignItems: "center" }}>
        <input type="checkbox" checked={forced || must} disabled={forced} onChange={e => setMust(e.target.checked)} />
        Must resolve with the issuer before signing {forced && <span style={{ color: "var(--text3)" }}>(always, on a Rejected row)</span>}
      </label>
      <div style={{ display: "flex", gap: 8 }}>
        <button style={btn(true)} disabled={busy || !text.trim()} onClick={async () => { if (await onCreate(review, text.trim(), forced || must)) { setText(""); setOpen(false); } }}>Add</button>
        <button style={btn(false)} onClick={() => { setOpen(false); setText(""); }}>Cancel</button>
      </div>
    </div>
  );
}

function ResolveForm({ issue, onResolve, busy }) {
  const [outcome, setOutcome] = useState("");
  const [notes, setNotes] = useState("");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: 10, background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 6, marginTop: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#9a3412" }}>Record the outcome agreed with the issuer</div>
      <select value={outcome} onChange={e => setOutcome(e.target.value)} style={input}>
        <option value="">Choose the outcome…</option>
        {ISSUE_OUTCOMES.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
      <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="What was agreed — the revised language, the compromise, or why the risk is being accepted" style={{ ...input, resize: "vertical", minHeight: 0 }} />
      <div><button style={btn(true)} disabled={busy || !outcome || !notes.trim()} onClick={() => onResolve(issue, outcome, notes.trim())}>Send to department for acceptance</button></div>
    </div>
  );
}

function ReopenForm({ issue, onReopen, onCancel, busy }) {
  const [note, setNote] = useState("");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
      <textarea rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="Why the outcome is not acceptable / what is still needed" style={{ ...input, resize: "vertical", minHeight: 0 }} />
      <div style={{ display: "flex", gap: 8 }}>
        <button style={btn(true)} disabled={busy || !note.trim()} onClick={() => onReopen(issue, note.trim())}>Reopen</button>
        <button style={btn(false)} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function IssueCard({ issue, review, canReview, canSales, locked, busy, handlers }) {
  const [reopening, setReopening] = useState(false);
  const must = isMustResolve(issue);
  const s = issue.Status;
  return (
    <div style={{ border: "1px solid var(--cs-border)", borderRadius: 6, padding: "10px 12px", background: "var(--surface)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
        <div style={{ fontSize: 13, color: "var(--text)", whiteSpace: "pre-wrap" }}>{issue.Description}</div>
        <StatusChip status={s} />
      </div>
      <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>
        Raised by {issue.RaisedBy || "—"} {when(issue.RaisedAt)}{!must && " · informational"}
      </div>
      {issue.ResolutionOutcome && (
        <div style={{ marginTop: 8, fontSize: 12, padding: "8px 10px", background: "#f8f9fb", borderRadius: 6 }}>
          <strong>{outcomeLabel(issue.ResolutionOutcome)}</strong>
          <span style={{ color: "var(--text3)" }}> — recorded by {issue.ResolvedBy || "—"} {when(issue.ResolvedAt)}</span>
          <div style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{issue.ResolutionNotes}</div>
        </div>
      )}
      {s === "reopened" && issue.ReopenNote && (
        <div style={{ marginTop: 8, fontSize: 12, padding: "8px 10px", background: "#fee2e2", borderRadius: 6, color: "#7f1d1d" }}>
          Reopened by {issue.ReopenedBy || "—"} {when(issue.ReopenedAt)}: <span style={{ whiteSpace: "pre-wrap" }}>{issue.ReopenNote}</span>
        </div>
      )}
      {s === "accepted" && (
        <div style={{ marginTop: 6, fontSize: 11, color: "#166534" }}>✓ Accepted by {issue.AcceptedBy || "—"} {when(issue.AcceptedAt)}</div>
      )}

      {!locked && (
        <>
          {must && (s === "open" || s === "reopened") && canSales && <ResolveForm issue={issue} onResolve={handlers.resolve} busy={busy} />}
          {must && (s === "open" || s === "reopened") && !canSales && (
            <div style={{ marginTop: 6, fontSize: 11, color: "var(--text3)" }}>Waiting on Sales to resolve this with the issuer.</div>
          )}
          {s === "proposed" && canReview && !reopening && (
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button style={btn(true)} disabled={busy} onClick={() => handlers.accept(issue)}>✓ Accept</button>
              <button style={btn(false)} disabled={busy} onClick={() => setReopening(true)}>Reopen…</button>
            </div>
          )}
          {s === "proposed" && !canReview && (
            <div style={{ marginTop: 6, fontSize: 11, color: "var(--text3)" }}>Waiting on {review.AssignedTo || review.Department} to accept or reopen.</div>
          )}
          {s === "accepted" && canReview && !reopening && (
            <div style={{ marginTop: 6 }}><button style={btn(false)} disabled={busy} onClick={() => setReopening(true)}>Reopen…</button></div>
          )}
          {reopening && <ReopenForm issue={issue} busy={busy} onCancel={() => setReopening(false)} onReopen={async (i, n) => { if (await handlers.reopen(i, n)) setReopening(false); }} />}
          {(s === "noted" || s === "open") && canReview && (
            <div style={{ marginTop: 6 }}><button style={{ ...btn(false), fontSize: 11 }} disabled={busy} onClick={() => handlers.remove(issue)}>Delete</button></div>
          )}
        </>
      )}
    </div>
  );
}

export default function ContractIssuesPanel({ reviews, issues, turnover, section3, documents, currentUser, isAdmin, busy, handlers }) {
  const byReview = issuesByReview(issues);
  const gate = contractGate({ reviews, issues, turnover, documents, section3 });
  const locked = gate.executed;
  const canSales = canResolveIssues(turnover, section3, currentUser, isAdmin);
  const summary = gate.mustResolveCount === 0
    ? "No must-resolve issues logged"
    : `${gate.accepted.length} of ${gate.mustResolveCount} must-resolve issue${gate.mustResolveCount === 1 ? "" : "s"} accepted`;

  return (
    <div style={{ marginTop: 20, border: "1px solid var(--cs-border)", borderRadius: 8, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", background: "#f8f9fb", borderBottom: "1px solid var(--cs-border)", display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Contract Issues — resolution with the issuer</div>
          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>
            Reviewers log each ask separately. Sales resolves must-resolve issues with the issuer; the raising department accepts the outcome. The contract can be signed once every must-resolve issue is accepted.
          </div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: gate.mustResolveCount && gate.accepted.length === gate.mustResolveCount ? "#166534" : "var(--text2)", whiteSpace: "nowrap" }}>
          {locked ? "🔒 Contract executed — issues closed" : summary}
        </div>
      </div>
      <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 12 }}>
        {reviews.map(r => {
          const own = byReview.get(r.ReviewID) || [];
          const canReview = canActForReview(r, currentUser, isAdmin);
          const showAdd = !locked && canReview && r.Status !== "Approved";
          if (own.length === 0 && !showAdd && r.Status !== "Rejected") return null;
          return (
            <div key={r.ReviewID}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>{r.Department}</div>
                <div style={{ fontSize: 11, color: r.Status === "Rejected" ? "#b91c1c" : "var(--text3)" }}>{r.Status}{r.AssignedTo ? ` · ${r.AssignedTo}` : ""}</div>
                <div style={{ flex: 1 }} />
                {showAdd && <AddIssue review={r} busy={busy} onCreate={handlers.create} />}
              </div>
              {r.Status === "Rejected" && own.length === 0 && (
                <div style={{ fontSize: 12, color: "#b91c1c", marginBottom: 6 }}>Rejected — {canReview ? "add an issue for each thing that must change." : `waiting on ${r.AssignedTo || r.Department} to log what must change.`}</div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {own.map(i => (
                  <IssueCard key={i.IssueID} issue={i} review={r} canReview={canReview} canSales={canSales} locked={locked} busy={busy} handlers={handlers} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
